import * as Popover from "@radix-ui/react-popover";
import { Check, ChevronDown, Search, X } from "lucide-react";
import type { ReactNode } from "react";
import { cx } from "./cx";

/**
 * A barra de filtro de toda lista geral: UMA linha com busca, filtro e período (decisão do Henrique, 21/10).
 * Os botões fechados dizem só "Filtro" e "Período"; com algo escolhido, mostram a escolha.
 */
export function BarraFiltros({ busca, aoBuscar, placeholder = "Buscar nesta lista", children, total }: {
  busca: string; aoBuscar: (v: string) => void; placeholder?: string; children: ReactNode; total?: ReactNode;
}) {
  return (
    <div className="flex items-center gap-2 pb-3">
      <label className="flex h-controle min-w-0 flex-1 items-center gap-2 rounded-controle bg-superficie-2 px-2 text-texto-2 focus-within:ring-2 focus-within:ring-foco/30 sm:max-w-72">
        <Search size={14} aria-hidden className="shrink-0 text-texto-3" />
        <input type="search" value={busca} onChange={(e) => aoBuscar(e.target.value)} placeholder={placeholder}
          className="min-w-0 flex-1 bg-transparent text-texto-1 outline-none" />
        {busca && (
          <button type="button" onClick={() => aoBuscar("")} className="grid size-5 cursor-pointer place-items-center text-texto-3 hover:text-texto-1">
            <X size={12} aria-hidden /><span className="sr-only">Limpar busca</span>
          </button>
        )}
      </label>
      {children}
      {total !== undefined && <span className="ml-auto hidden text-meta whitespace-nowrap text-texto-3 sm:block">{total}</span>}
    </div>
  );
}

/** Botão da barra que abre um menu. `valor` vazio = nada escolhido (mostra só o rótulo). */
export function MenuFiltro({ rotulo, valor, children, abertoInicial }: { rotulo: string; valor?: string | null; children: ReactNode; abertoInicial?: boolean }) {
  return (
    <Popover.Root defaultOpen={abertoInicial}>
      <Popover.Trigger
        className={cx("inline-flex h-controle shrink-0 cursor-pointer items-center gap-1 rounded-controle px-2 whitespace-nowrap hover:bg-superficie-2",
          valor ? "bg-superficie-2 font-medium text-texto-1" : "text-texto-2")}>
        <span className="max-w-40 truncate">{valor || rotulo}</span>
        <ChevronDown size={14} aria-hidden className="text-texto-3" />
      </Popover.Trigger>
      <Popover.Portal>
        <Popover.Content align="start" sideOffset={4} collisionPadding={8}
          className="z-50 w-64 max-w-[calc(100vw-1rem)] rounded-cartao bg-flutuante p-1 text-corpo shadow-flutuante"> {/* design-ok: largura relativa à janela */}
          {children}
        </Popover.Content>
      </Popover.Portal>
    </Popover.Root>
  );
}

/** Título de grupo dentro do menu. */
export function GrupoMenu({ titulo, children }: { titulo?: string; children: ReactNode }) {
  return (
    <div role="group" aria-label={titulo} className="py-1">
      {titulo && <p className="px-2 pb-1 text-meta font-medium text-texto-3">{titulo}</p>}
      {children}
    </div>
  );
}

/** Opção marcável dentro do menu (uma por grupo). */
export function OpcaoMenu({ marcada, aoEscolher, children, contagem }: {
  marcada: boolean; aoEscolher: () => void; children: ReactNode; contagem?: number;
}) {
  return (
    <button type="button" role="menuitemradio" aria-checked={marcada} onClick={aoEscolher}
      className="flex h-controle w-full cursor-pointer items-center gap-2 rounded-controle px-2 text-left text-texto-1 hover:bg-superficie-2">
      <span className="grid size-4 place-items-center text-texto-1">{marcada && <Check size={14} aria-hidden />}</span>
      <span className="flex-1 truncate">{children}</span>
      {contagem !== undefined && <span className="numeros text-meta text-texto-3">{contagem}</span>}
    </button>
  );
}
